import React, { useMemo, useRef } from 'react'

import { Axis, AxisScale, TickFormatter } from '@visx/axis'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { ParentSize } from '@visx/responsive'
import { scaleBand, scaleLinear } from '@visx/scale'

import ResizeObserverPolyfill from 'resize-observer-polyfill'

import {
  AnimatedBars,
  ChartData,
  dmyFormatter,
  getDatasetStacksArray,
  SVGDatumType,
  TimelineDatum,
} from '..'

import { LineComponent } from '../Quantitative/PathComponents'

import { AppTheme, Box, RoundedPlainTextButton, useAppTheme } from '../..'

import { flatten, throttle, uniq } from 'lodash-es'

import { extent, ScaleLinear } from 'd3'

import {
  getBarGroupData,
  getBarGroupDataLabel,
  getColorAccessor,
  getDatasetTotalsByLabel,
  getLinearScaleRatePercentageAccessor,
  getMultiChartTooltipData,
  getPrimaryYTickLabelProps,
  getScaleBandMidPointAccessor,
  getSecondaryYTickLabelProps,
  getXTickLabelProps,
  invertBandScale,
  normaliseDomain,
} from './lib'

import { displayNumber, downloadSVG } from '@percept/utils'

import { MultiChartProps } from './typings'
import { getLegendConfigs, MultiChartLegend } from './MultiChartLegend'
import { AxisProps } from '@visx/axis/lib/axis/Axis'
import { CloudDownload } from '@material-ui/icons'
import { BarGroup } from '@visx/shape'
import { useTooltip, useTooltipInPortal } from '@visx/tooltip'
import { localPoint } from '@visx/event'
import { MultiChartTooltip, MultiChartTooltipData } from './MultiChartTooltip'



type AxisConfiguration = {
  yScale: ScaleLinear<number, number>
  yTickFormatter: TickFormatter<any>
  label?: string
}

const tooltipStyles: React.CSSProperties = {
  margin: 0,
  padding: 0,
  background: 'none',
  pointerEvents: 'none',
  position: 'absolute',
  lineHeight: 1,
  zIndex: 1099,
}


export function MultiChartSVG<T extends SVGDatumType = TimelineDatum>({
  width,
  height,
  barChartVariant = 'group',
  barChartDatasets,
  lineChartDatasets,
  barChartDomain,
  lineChartDomain,
  xTickFormatter = dmyFormatter,
  yTickFormatterBarChart = displayNumber,
  yTickFormatterLineChart = displayNumber,
  barChartAxisLabel,
  lineChartAxisLabel,
  LineComponentProps = {},
  svgRef,
  defs,
  title,
}: MultiChartProps<T>) {

  const appTheme = useAppTheme()
  
  const canonicalXOffset = 72

  const verticalMargin = 80

  let titleHeight = 0
  if( title ){
    titleHeight = 20
  }

  const xMax = width - canonicalXOffset

  const yMax = height - verticalMargin

  const primaryDatasets = !barChartDatasets ? lineChartDatasets : barChartDatasets
  const secondaryDatasets = barChartDatasets && lineChartDatasets ? lineChartDatasets : undefined

  const isBarChartPrimary = !!barChartDatasets

  const primaryDataArrays = (primaryDatasets || []).map( d => d.data )
  const secondaryDataArrays = (secondaryDatasets || []).map( d => d.data )

  const combinedData: ChartData[] = [
    ...primaryDataArrays,
    ...secondaryDataArrays,
  ]

  const legendConfigs = useMemo(() => {
    return getLegendConfigs({ barChartDatasets, lineChartDatasets, width: width - (canonicalXOffset * 4) })
  }, [barChartDatasets, lineChartDatasets, width, canonicalXOffset])

  let legendHeight = 0
  if( legendConfigs.length ){
    const lastLegendItem = legendConfigs[legendConfigs.length - 1]
    const maxYOffset =  lastLegendItem.y
    legendHeight = maxYOffset + lastLegendItem.height
  }

  const xDomain = uniq(
    flatten(
      combinedData
    ).map( d => Number(d.label))
  ).sort()

  const xScale = scaleBand<number>({
    range: [canonicalXOffset, xMax],
    round: true,
    domain: xDomain,
    padding: 0.2,
  })

  const barGroupKeys = (barChartDatasets || []).map( d => d.key)

  const groupScale = scaleBand<string>({
    domain: barGroupKeys,
    padding: 0.1,
    range: [0, xScale.bandwidth()],
  })

  const isGroupedBarChart = barChartVariant === 'group'

  const barGroupData = (
    barChartDatasets && isGroupedBarChart ?
      getBarGroupData(barChartDatasets) :
      null
  )

  let maxBarChartValue = 0
  let allValues: number[] = []
  if( barChartDatasets ){
    if( isGroupedBarChart ){
      allValues = flatten(barChartDatasets.map( ds => ds.data.map( d => Number(d.value || 0))))
    }else{
      allValues = Object.values(getDatasetTotalsByLabel(barChartDatasets))
    }
    maxBarChartValue = Math.max(...allValues)
  }

  const yScaleBarChart = scaleLinear<number>({
    range: [yMax, 0],
    domain: normaliseDomain(barChartDomain || [0, maxBarChartValue]),
    nice: true,
  })

  const lineChartExtent = (
    lineChartDomain ||
    extent(
      flatten((lineChartDatasets || []).map( d => d.data)).map( d => Number(d.value) )
    )
  )

  const resolvedLineChartDomain: [number, number] = (
    lineChartExtent[0] !== undefined && lineChartExtent[1] !== undefined ?
      [lineChartExtent[0], lineChartExtent[1]] :
      [0, 1]
  )

  const yScaleLineChart = scaleLinear<number>({
    range: [yMax, 0],
    domain: resolvedLineChartDomain,
    nice: true,
  })

  const axisConfigurations: Record<'barChart' | 'lineChart', AxisConfiguration> = {
    barChart: {
      yScale: yScaleBarChart,
      yTickFormatter: yTickFormatterBarChart,
      label: barChartAxisLabel || undefined,
    },
    lineChart: {
      yScale: yScaleLineChart,
      yTickFormatter: yTickFormatterLineChart,
      label: lineChartAxisLabel || undefined,
    }
  }

  const primaryAxisConfig = isBarChartPrimary ? axisConfigurations.barChart : axisConfigurations.lineChart
  const secondaryAxisConfig = !!secondaryDatasets && axisConfigurations.lineChart

  const yScalePrimary = isBarChartPrimary ? yScaleBarChart : yScaleLineChart

  const yScaleSecondary = yScaleLineChart

  const datasetStacksArray = (
    !barChartDatasets || isGroupedBarChart ?
      [] :
      getDatasetStacksArray({
        datasets: barChartDatasets,
        yMax,
        yScale: yScalePrimary,
        xDomain,
        xScale,
      })
  )

  const pathComponentXAccessor = getScaleBandMidPointAccessor(xScale)
  const pathComponentYAccessor = getLinearScaleRatePercentageAccessor(yScaleSecondary)

  const commonAxisProps: Partial<AxisProps<AxisScale>> = {
    stroke: appTheme.chart.axis.stroke,
    tickLength: 8,
    tickStroke: appTheme.chart.tick.stroke,
    labelOffset: 50,
    labelProps: {
      fontFamily: appTheme.typography.fontFamily,
      textAnchor: 'middle',
      fontSize: 11,
      fill: 'currentColor',
    },
  }

  const colorAccessor = getColorAccessor(barChartDatasets || [])

  const {
    tooltipOpen,
    tooltipLeft = 0,
    tooltipTop = 0,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<MultiChartTooltipData>()

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
    polyfill: ResizeObserverPolyfill,
  })

  const handleTooltip = (e: React.MouseEvent) => {
  
    const { x, y } = localPoint(e) || { x: 0, y: 0 }

    const x0 = invertBandScale(xScale, x)

    const tooltipData = getMultiChartTooltipData({
      label: x0,
      xTickFormatter,
      barChartDatasets,
      lineChartDatasets,
      yTickFormatterBarChart,
      yTickFormatterLineChart,
    })

    if( tooltipData.data.length ){
      showTooltip({
        tooltipData,
        tooltipLeft: xScale(x0),
        tooltipTop: 0,
      })
    }

    return
  }

  const handleTooltipThrottled = throttle(handleTooltip, 200)

  return (
    <>
    <svg
      ref={(el) => {
        if( svgRef ){
          svgRef.current = el
        }
        containerRef(el)
      }}
      width={width}
      height={height + legendHeight}>
      { !!defs && <defs>{ defs }</defs>}
      { title && (
        <text x={width / 2} y={titleHeight / 2} textAnchor='middle' fill='currentColor'>{ title }</text>
      )}
      <g transform={`translate(0,${titleHeight})`}>
        <GridRows
          left={canonicalXOffset}
          scale={yScalePrimary}
          width={xMax - canonicalXOffset}
          height={yMax}
          stroke={appTheme.chart.grid.stroke} />

        <Axis
          orientation='bottom'
          scale={xScale}
          tickLabelProps={getXTickLabelProps}
          tickFormat={xTickFormatter}
          top={Number(yScalePrimary(yScalePrimary.domain()[0]))}
          {...commonAxisProps} />

        { primaryAxisConfig && (
          <Axis
            orientation='left'
            scale={primaryAxisConfig.yScale as AxisScale}
            left={canonicalXOffset}
            tickLabelProps={getPrimaryYTickLabelProps}
            tickFormat={primaryAxisConfig.yTickFormatter}
            label={primaryAxisConfig.label}
            {...commonAxisProps} />
        )}

        { secondaryAxisConfig && (
          <Axis
            orientation='right'
            scale={secondaryAxisConfig.yScale as AxisScale}
            left={width - canonicalXOffset}
            tickLabelProps={getSecondaryYTickLabelProps}
            tickFormat={secondaryAxisConfig.yTickFormatter}
            label={secondaryAxisConfig.label}
            {...commonAxisProps} />
        )}

        { !!datasetStacksArray.length && (
          <Group>
            { datasetStacksArray.map( ([dataset, bars], i) => (
              <AnimatedBars
                key={`${dataset.key}-stack-${i}`}
                bars={bars}
                animate={false}
                animateInitial={false}
                includeBarBackground={false}
                xScale={xScale}
                yScale={yScaleBarChart}
                maxHeight={yMax} />
            ))}
          </Group>
        )}

        { barGroupData && (
          <Group>
            <BarGroup
              data={barGroupData}
              // color={colorScale}
              color={colorAccessor}
              keys={barGroupKeys}
              x0={getBarGroupDataLabel}
              x0Scale={xScale}
              x1Scale={groupScale}
              yScale={yScaleBarChart}
              height={yMax}>
              {(barGroups) =>
                barGroups.map((barGroup) => (
                  <Group key={`bar-group-${barGroup.index}-${barGroup.x0}`} left={barGroup.x0}>
                    {barGroup.bars.map((bar) => (
                      <rect
                        key={`bar-group-bar-${barGroup.index}-${bar.index}-${bar.value}-${bar.key}`}
                        x={bar.x}
                        y={bar.y}
                        width={bar.width}
                        height={bar.height}
                        fill={bar.color} />
                    ))}
                  </Group>
                ))
              }
            </BarGroup>
          </Group>
        )}

        { lineChartDatasets && lineChartDatasets.map( dataset => {
          return (
            <Group key={`${dataset.key}-path`}>
              <LineComponent
                data={dataset.data}
                animate={false}
                animateInitial={false}
                labels={xDomain}
                stroke={dataset.color}
                xScale={xScale}
                yScale={yScaleLineChart}
                width={xMax}
                height={yMax}
                xAccessor={pathComponentXAccessor}
                yAccessor={pathComponentYAccessor}
                {...LineComponentProps}
                svgPathProps={dataset.svgPathProps || LineComponentProps.svgPathProps} />
            </Group>
          )
        })}

        <rect
          x={canonicalXOffset}
          y={0}
          width={xMax}
          height={yMax}
          fill='#FFF'
          opacity={0}
          onMouseMove={handleTooltipThrottled}
          onMouseOut={hideTooltip} />

        <MultiChartLegend
          legends={legendConfigs}
          width={width - canonicalXOffset}
          height={legendHeight}
          x={canonicalXOffset}
          y={height - 24}
          overflow='visible' />
      </g>

    </svg>

    { tooltipOpen && tooltipData && (
      <TooltipInPortal
        key={Math.random()}
        top={tooltipTop}
        left={tooltipLeft}
        style={tooltipStyles}>

        <MultiChartTooltip
          tooltipData={tooltipData} />

      </TooltipInPortal>
    )}

    </>
  )
}

export function MultiChart<T extends SVGDatumType = TimelineDatum>(
  props: Omit<MultiChartProps<T>, 'width' | 'height'> & Partial<Pick<MultiChartProps<T>, 'width' | 'height'> >
) {
  return (
    <ParentSize>
      {({ width, height }) => (
        <MultiChartSVG width={width} height={height} {...props} />
      )}
    </ParentSize>
  )
}

const downloadMultiChartSVG = (
  element: SVGSVGElement,
  fileName: string,
  appTheme: AppTheme
): Promise<void> => {
  return downloadSVG(
    element,
    fileName,
    {
      wrappingElement: {
        margin: {
          top: 20,
          left: 20,
          bottom: 20,
          right: 20,
        },
        attributes: {
          overflow: 'visible',
        },
      },
      // elementFilter: (el) => {
      //   const classList: string[] = Array.prototype.slice.call(el.classList)
      //   return !classList.includes(TOOLTIP_CSS_CLASSNAME)
      // },
      elementModifier: (el) => {
        el.setAttribute('overflow', 'visible')
      },
      fontFamilyOverride: appTheme.typography.fontFamily,
    },
  )
}


export function ExportableMultiChart<T extends SVGDatumType = TimelineDatum>(
  props: Omit<MultiChartProps<T>, 'width' | 'height'> & Partial<Pick<MultiChartProps<T>, 'width' | 'height'> >
): JSX.Element {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const appTheme = useAppTheme()
  return (
    <div>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='flex-end'
        mb={2}>
        <RoundedPlainTextButton
          size='small'
          variant='contained'
          startIcon={<CloudDownload />}
          onClick={(): void => {
            if( svgRef.current ){
              downloadMultiChartSVG(
                svgRef.current, `${props.title || 'chart'}.svg`, appTheme
              ).then(console.log)
            }
          }}>
          Download
        </RoundedPlainTextButton>
      </Box>
      <MultiChart svgRef={svgRef} {...props} />
    </div>
  )
}
